import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { diffBetweenDays } from '@app/core/date-utils';

import {
  IGetPatientListRequest,
  mergeSearchByUserFromStoredSearchType,
  SearchType,
} from '@app/core/patient-requests';
import { UsersService } from '@app/core/users';
import { MsgboxService } from '@app/shared/msgbox';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';

export interface IPatientSearchInfo {
  maxSearchDays: number;
  defaultSearch: IGetPatientListRequest;
  previousSearch: IGetPatientListRequest;
}

interface IOption {
  id: string;
  description: string;
}

@Component({
  selector: 'app-search-by-patient',
  templateUrl: './search-by-patient.component.html',
  styleUrls: ['./search-by-patient.component.css'],
})
export class SearchByPatientComponent implements OnInit, OnDestroy {
  @Input()
  public searchInfo: IPatientSearchInfo;

  public today = new Date();

  @Output()
  public onSearch: EventEmitter<IGetPatientListRequest>;

  public form: FormGroup;
  public genders: IOption[];

  private destroyed$ = new Subject<void>();

  public sisifoUserHasRelatedDoctor$: Subject<boolean>;
  public sisifoUserHasRelatedCenter$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  private DISABLE_CONDITIONS = {
    history: ['externalHistory', 'lastName', 'name', 'gender', 'birthDate'],
    externalHistory: ['history', 'lastName', 'name', 'gender', 'birthDate'],
    lastName: ['history', 'externalHistory'],
    name: ['history', 'externalHistory'],
    gender: ['history', 'externalHistory'],
    birthDate: ['history', 'externalHistory'],
    extractionFrom: ['history', 'externalHistory'],
    extractionTo: ['history', 'externalHistory'],
    searchByUser: ['history', 'externalHistory'],
  };

  constructor(
    private msgbox: MsgboxService,
    private _userService: UsersService
  ) {
    this.onSearch = new EventEmitter();

    this.genders = [
      {
        id: 'M',
        description: 'Masculino',
      },
      {
        id: 'F',
        description: 'Femenino',
      },
    ];

    this.form = new FormGroup({
      history: new FormControl(''),
      lastName: new FormControl(),
      name: new FormControl(),
      externalHistory: new FormControl(),
      gender: new FormControl(),
      birthDate: new FormControl(),
      extractionFrom: new FormControl(),
      extractionTo: new FormControl(),
      searchByUser: new FormControl(false),
    });
  }

  enableDisableFields() {
    Object.keys(this.DISABLE_CONDITIONS).forEach((key) => {
      const conditionArray = this.DISABLE_CONDITIONS[key];
      const field = this.form.get(key);
      const conditionAccomplished = conditionArray.some(
        (condition) => !!this.form.get(condition).value
      );
      if (conditionAccomplished) {
        field.disable({ emitEvent: false }); // Evita ciclo
      } else {
        field.enable({ emitEvent: false }); // Evita ciclo
      }
    });
  }

  ngOnInit() {
    // Observa cambios en 'history'
    this.form.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((historyValue) => {
        this.enableDisableFields();
      });

    // retrieve user data to check if the user has a related doctor
    this.getSisifoUserHasRelatedDoctor();
  }

  disableFields(fields: string[]) {
    fields.forEach((field) =>
      this.form.get(field).disable({ emitEvent: false })
    ); // Evita ciclo
  }

  enableFields(fields: string[]) {
    fields.forEach((field) =>
      this.form.get(field).enable({ emitEvent: false })
    ); // Evita ciclo
  }

  areFieldsFilled(fields: string[]): boolean {
    return fields.some((field) => !!this.form.get(field).value);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchInfo'].currentValue) {
      const raw = this.searchInfo.previousSearch || this.searchInfo.defaultSearch;
      this.form.patchValue(mergeSearchByUserFromStoredSearchType(raw as Record<string, unknown>), {
        emitEvent: false,
      }); // No dispara valueChanges

      this.enableDisableFields();
    }
  }

  // comprueba si el usuario tiene facultativo asociado
  getSisifoUserHasRelatedDoctor(): void {
    this.sisifoUserHasRelatedDoctor$ = new Subject<boolean>();
    this._userService.getSisifoUserData().subscribe((userData) => {
      this.sisifoUserHasRelatedDoctor$.next(!!userData.arztNr);
      this.sisifoUserHasRelatedCenter$.next(!!userData.centers?.length);
    });
  }

  buildPayload(): IGetPatientListRequest {
    const _result = {
      ...this.form.value,
      searchType: this.form.value?.searchByUser
        ? SearchType.User
        : SearchType.Center,
    };
    delete _result.searchByUser;
    return _result;
  }

  public search(): void {
    this.form.markAllAsTouched();
    if (!this.form.valid) {
      return;
    }

    //si género, comprobar que hay más parámetros de búsqueda, WIR-RF-01.01.10.2
    if (this.form.value.gender) {
      if (
        Object.keys(this.form.value).filter(
          (key) => key !== 'gender' && this.form.value[key]
        ).length < 3
      ) {
        this.msgbox.showError('WEB_RESULTS.ERROR.SEX_NOT_ONLY_CRITERIA');
        return;
      }
    }

    if (this.form.value.extractionFrom && this.form.value.extractionTo) {
      if (
        Object.keys(this.form.value).filter((key) => this.form.value[key])
          .length < 3
      ) {
        this.msgbox.showError('WEB_RESULTS.ERROR.MIN_ONE_CRITERIA');
        return;
      }
    }

    if (this.form.value.extractionTo && this.form.value.extractionFrom) {
      if (
        diffBetweenDays(
          this.form.value.extractionTo,
          this.form.value.extractionFrom
        ) > this.searchInfo.maxSearchDays
      ) {
        this.msgbox.showError('WEB_RESULTS.ERROR.MAX_EXTRACTION_RANGE', {
          value: this.searchInfo.maxSearchDays,
        });
        return;
      }
    }

    this.onSearch.next(this.buildPayload());
  }

  public clear(): void {
    this.form.reset();
    this.form.patchValue(this.searchInfo.defaultSearch);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
  }
}
